// Edición de imagen (webhook `editor_ia` → atlas `/v1/images/edit`) y upscale
// (webhook `upscale_imagen` → atlas `/v1/images/upscale`) — ADR-601.
// Flag ADENTRO del helper:
//   ON  → atlas con JWT (`user_id` NO viaja). Respuestas idénticas:
//         edit `{response:'success', edited_image_id}` y moderación
//         `{response:'error_moderation'}` en 200 (el caller la maneja);
//         upscale `{success:true, upscaled_path}` / 402 `{success:false,
//         error:'insufficient_credits', required}`. Sin créditos en edit =
//         402 tipado → GenerateAdError billing.
//   OFF → webhooks n8n byte-idénticos (upscale conserva el proxy `/api/n8n`
//         en dev).

import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { GenerateAdError } from './generateAdRequest';
import { supabase } from './supabase';

const EDITOR_IA_URL = 'https://ai-agency-dt7d.onrender.com/webhook/editor_ia';

// Mismo copy de saldo que las generaciones de imagen (generateAdRequest).
const BILLING_MESSAGE =
  'No te alcanzan los créditos para esta generación. Sumá créditos o actualizá tu plan para continuar.';

/**
 * Etiqueta de analytics por MODO de edición (pedido de Matías: distinguir en
 * el panel qué tipo de edición se usó, no solo la pantalla). La usa
 * EditarImagen; el estudio y el planificador conservan su etiqueta de
 * superficie ('estudio_edicion' / 'planificador_video').
 */
export const MODO_UTILIZADO_EDICION: Record<string, string> = {
  edit_general: 'editar_imagen_general',
  edit_preciso: 'editar_imagen_precisa',
  cambio_producto: 'editar_imagen_producto',
  cambiar_angulo: 'editar_imagen_angulo',
};

export interface ImageEditBody {
  user_id: string;
  imagen_id: string | number;
  mode: string;
  instruction?: string;
  mask?: string;
  variante_id?: string | number;
  horizontal_angle?: number | string;
  vertical_angle?: number | string;
  zoom?: number | string;
  modo_utilizado?: string;
}

export interface ImageEditResponse {
  response?: string;
  edited_image_id?: string | number;
  imagen_id?: string | number;
  id?: string | number;
  error?: string;
}

// b64 (con o sin prefijo data:) → Blob para subir la máscara al bucket.
function b64ToBlob(b64: string, type = 'image/png'): Blob {
  const raw = b64.includes(',') ? b64.slice(b64.indexOf(',') + 1) : b64;
  const bytes = atob(raw);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type });
}

/**
 * Edita una imagen. Devuelve el JSON parseado (incluida la moderación
 * `{response:'error_moderation'}` — la maneja cada caller). Tira
 * GenerateAdError billing en 402 y Error genérico en otros HTTP.
 *
 * `opts.companyId` es OBLIGATORIO si el body trae `mask` (edit_preciso) y el
 * flag atlas está ON: la máscara ya no viaja en el body (la cola del backend
 * rechaza binarios) — se sube al bucket `generadas` bajo el prefijo de la
 * empresa y viaja `mask_key` RELATIVA (sin ese prefijo). El fallback n8n
 * sigue mandando el b64 como siempre.
 */
export async function postImageEdit(
  body: ImageEditBody,
  opts?: { companyId?: string },
): Promise<ImageEditResponse> {
  let res: Response;
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, mask, ...atlasBody } = body;
    if (mask) {
      if (!opts?.companyId) {
        throw new Error(
          'No se pudo identificar la empresa para subir la máscara.',
        );
      }
      const maskKey = `masks/edit_${Date.now()}.png`;
      const { error: upErr } = await supabase.storage
        .from('generadas')
        .upload(`${opts.companyId}/${maskKey}`, b64ToBlob(mask), {
          contentType: 'image/png',
          upsert: false,
        });
      if (upErr) {
        console.error('[imageEdit] upload de máscara falló', upErr);
        throw new Error('No se pudo subir la máscara. Intentá de nuevo.');
      }
      (atlasBody as Record<string, unknown>).mask_key = maskKey;
    }
    res = await atlasBackendFetch('/v1/images/edit', atlasBody);
    if (res.status === 402) {
      throw new GenerateAdError('billing', BILLING_MESSAGE, 'billing');
    }
  } else {
    res = await fetch(EDITOR_IA_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
  }
  if (!res.ok) {
    throw new Error('No se pudo editar la imagen. Intentá de nuevo.');
  }
  return (await res.json()) as ImageEditResponse;
}

export interface ImageUpscaleResponse {
  success?: boolean;
  upscaled_path?: string;
  error?: string;
  required?: number;
  plan?: string;
}

/**
 * Upscale de una imagen generada. Devuelve el JSON parseado en TODOS los
 * casos (éxito, `insufficient_credits`, etc.) — los callers ya branchean por
 * `data.success` / `data.error`, y el 402 del atlas trae el mismo shape de
 * error que n8n devolvía en 200.
 */
export async function postImageUpscale(body: {
  user_id: string;
  image_id: string | number;
  scale: number;
}): Promise<ImageUpscaleResponse> {
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, ...atlasBody } = body;
    const res = await atlasBackendFetch('/v1/images/upscale', atlasBody);
    return (await res.json()) as ImageUpscaleResponse;
  }
  // Proxy local en dev (CORS del webhook), igual que siempre.
  const n8nBase = import.meta.env.DEV
    ? '/api/n8n'
    : 'https://ai-agency-dt7d.onrender.com';
  const res = await fetch(`${n8nBase}/webhook/upscale_imagen`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify(body),
  });
  return (await res.json()) as ImageUpscaleResponse;
}
